import {
    Blocks,
    Box,
    Braces,
    BotMessageSquare,
    Brain,
    CircleX,
    CircuitBoard,
    Cpu,
    File,
    FileJson2,
    FileStack,
    FileText,
    Folder,
    FolderClosed,
    FolderOpen,
    FolderTree,
    Folders,
    Image,
    Layers,
    Layers3,
    List,
    ListTree,
    MessageCircle,
    MessageSquareCode,
    MessagesSquare,
    NotebookText,
    Package,
    Package2,
    Play,
    Plug,
    PlugZap,
    Puzzle,
    ScrollText,
    Search,
    Send,
    Server,
    ServerCog,
    Sparkles,
    Terminal,
    Upload,
    WandSparkles,
    X,
} from "lucide-react";
import type { CSSProperties } from "react";
import type { FileIconDefinition, ProductIconThemeIcon } from "../contracts";
import type { ProductIconSlotId } from "../registry/icon-slot-registry";
import {
    getFileIconThemeManifest,
    getProductIconThemeManifest,
} from "./theme-resolver";
import { useThemeStore } from "./theme-store";

const ICON_COMPONENTS = {
    Blocks,
    Box,
    Braces,
    BotMessageSquare,
    Brain,
    CircleX,
    CircuitBoard,
    Cpu,
    File,
    FileJson2,
    FileStack,
    FileText,
    Folder,
    FolderClosed,
    FolderOpen,
    FolderTree,
    Folders,
    Image,
    Layers,
    Layers3,
    List,
    ListTree,
    MessageCircle,
    MessageSquareCode,
    MessagesSquare,
    NotebookText,
    Package,
    Package2,
    Play,
    Plug,
    PlugZap,
    Puzzle,
    ScrollText,
    Search,
    Send,
    Server,
    ServerCog,
    Sparkles,
    Terminal,
    Upload,
    WandSparkles,
    X,
} as const;

type IconName = keyof typeof ICON_COMPONENTS;
type IconColorMode = "theme" | "currentColor";

export function getThemeIconComponent(
    name: string | undefined,
    fallback: IconName = "File",
) {
    return ICON_COMPONENTS[toIconName(name, fallback)];
}

export interface ProductIconProps {
    slot: ProductIconSlotId;
    size?: number;
    className?: string;
    style?: CSSProperties;
    fallback?: IconName;
}

export interface FileIconProps {
    name: string;
    isDirectory?: boolean;
    isOpen?: boolean;
    isWorkspaceRoot?: boolean;
    size?: number;
    className?: string;
    style?: CSSProperties;
    colorMode?: IconColorMode;
}

export function ProductIcon({
    slot,
    size = 16,
    className,
    style,
    fallback = "File",
}: ProductIconProps) {
    const themeId = useThemeStore((state) => state.productIconTheme);
    const spec = resolveProductIconSpec(themeId, slot);
    return renderIcon(spec, fallback, size, className, style);
}

export function FileIcon({
    name,
    isDirectory = false,
    isOpen = false,
    isWorkspaceRoot = false,
    size = 16,
    className,
    style,
    colorMode = "theme",
}: FileIconProps) {
    const themeId = useThemeStore((state) => state.fileIconTheme);
    const spec = resolveFileIconSpec(themeId, {
        name,
        isDirectory,
        isOpen,
        isWorkspaceRoot,
    });
    return renderIcon(spec, "File", size, className, style, colorMode);
}

export function resolveProductIconSpec(
    themeId: string,
    slot: ProductIconSlotId,
): ProductIconThemeIcon | undefined {
    return getProductIconThemeManifest(themeId).icons[slot];
}

export function resolveFileIconSpec(
    themeId: string,
    params: {
        name: string;
        isDirectory?: boolean;
        isOpen?: boolean;
        isWorkspaceRoot?: boolean;
    },
): FileIconDefinition | undefined {
    const manifest = getFileIconThemeManifest(themeId);
    const normalizedName = params.name.toLowerCase();

    if (params.isDirectory) {
        if (params.isWorkspaceRoot) {
            return manifest.folders?.workspace ?? manifest.folders?.open;
        }
        return params.isOpen ? manifest.folders?.open : manifest.folders?.closed;
    }

    const exactMatch = manifest.exactNames?.[normalizedName];
    if (exactMatch) return exactMatch;

    if (normalizedName.includes(".")) {
        const parts = normalizedName.split(".").filter(Boolean);
        for (let index = parts.length - 1; index > 0; index -= 1) {
            const extension = parts[index];
            if (extension && manifest.extensions?.[extension]) {
                return manifest.extensions[extension];
            }
        }
    }

    return manifest.fallbackFile;
}

function renderIcon(
    spec: ProductIconThemeIcon | FileIconDefinition | undefined,
    fallback: IconName,
    size: number,
    className?: string,
    style?: CSSProperties,
    colorMode: IconColorMode = "theme",
) {
    const Icon = ICON_COMPONENTS[toIconName(spec?.name, fallback)];
    return (
        <Icon
            size={size}
            className={className}
            style={colorMode === "theme" && spec?.color ? { ...style, color: spec.color } : style}
        />
    );
}

function toIconName(
    name: string | undefined,
    fallback: IconName,
): IconName {
    if (!name) return fallback;
    return name in ICON_COMPONENTS ? (name as IconName) : fallback;
}
